// src/app/contact/page.tsx - Contact page with form and information
'use client';

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  IconButton,
  Divider,
  Alert,
  Skeleton,
  Paper,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Email,
  Phone,
  LocationOn,
  Send,
  LinkedIn,
  Twitter,
  Facebook,
  YouTube,
  GitHub,
  Instagram,
  Business,
  AccessTime,
  Directions,
} from '@mui/icons-material';

// Import API functions and types
import { api, type SiteSettings } from '@/lib/api';

// Import our modular color system
import { getActiveColorTheme, withOpacity } from '@/lib/colors';

// =====================
// STYLED COMPONENTS
// =====================

const HeroSection = styled(Box)(() => {
  const colors = getActiveColorTheme();
  return {
    minHeight: '40vh',
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    background: colors.background.hero,
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'url("/api/placeholder/1920/600") center/cover',
      opacity: 0.1,
      zIndex: 1,
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: colors.effects.overlay,
      zIndex: 2,
    }
  };
});

const HeroContent = styled(Box)(() => {
  const colors = getActiveColorTheme();
  return {
    position: 'relative',
    zIndex: 3,
    color: colors.text.primary,
    textAlign: 'center',
  };
});

const ContactCard = styled(Card)(() => {
  const colors = getActiveColorTheme();
  return {
    background: colors.gradients.card,
    border: `1px solid ${colors.border.primary}`,
    borderRadius: '16px',
    transition: 'all 0.3s ease',
    display: 'flex',
    flexDirection: 'column',
    
    '&:hover': {
      borderColor: colors.border.accent,
      transform: 'translateY(-2px)',
      boxShadow: `0 8px 25px ${colors.effects.glow}`,
    },
  };
});

const ContactFormCard = styled(Card)(() => {
  const colors = getActiveColorTheme();
  return {
    background: colors.background.paper,
    border: `1px solid ${colors.border.primary}`,
    borderRadius: '16px',
    padding: '32px',
  };
});

const StyledTextField = styled(TextField)(() => {
  const colors = getActiveColorTheme();
  return {
    '& .MuiOutlinedInput-root': {
      color: colors.text.primary,
      backgroundColor: colors.background.elevated,
      '& fieldset': {
        borderColor: colors.border.primary,
      },
      '&:hover fieldset': {
        borderColor: colors.border.accent,
      },
      '&.Mui-focused fieldset': {
        borderColor: colors.brand.accent.main,
      },
    },
    '& .MuiInputLabel-root': {
      color: colors.text.secondary,
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: colors.brand.accent.main,
    },
  };
});

const SubmitButton = styled(Button)(() => {
  const colors = getActiveColorTheme();
  return {
    background: colors.gradients.primary,
    color: colors.brand.accent.contrastText,
    padding: '12px 32px',
    borderRadius: '8px',
    fontWeight: 600,
    fontSize: '1rem',
    textTransform: 'none',
    '&:hover': {
      background: colors.brand.accent.dark,
      transform: 'translateY(-1px)',
      boxShadow: `0 4px 12px ${withOpacity(colors.brand.accent.main, 0.3)}`,
    },
    '&:disabled': {
      background: colors.text.disabled,
      color: colors.background.paper,
    },
  };
});

const SocialIconButton = styled(IconButton)(() => {
  const colors = getActiveColorTheme();
  return {
    color: colors.text.secondary,
    padding: 8,
    border: `2px solid ${withOpacity(colors.brand.accent.main, 0.3)}`,
    margin: '0 4px',
    backgroundColor: withOpacity(colors.brand.accent.main, 0.05),
    '&:hover': {
      color: colors.brand.accent.main,
      borderColor: colors.brand.accent.main,
      backgroundColor: withOpacity(colors.brand.accent.main, 0.1),
      transform: 'translateY(-2px)',
    },
  };
});

const InfoCard = styled(Paper)(() => {
  const colors = getActiveColorTheme();
  return {
    background: colors.background.elevated,
    border: `1px solid ${colors.border.secondary}`,
    borderRadius: '12px',
    padding: '24px',
    textAlign: 'center',
    transition: 'all 0.3s ease',
    '&:hover': {
      borderColor: colors.border.accent,
      transform: 'translateY(-2px)',
    },
  };
});

// =====================
// INTERFACES
// =====================

interface ContactFormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

// =====================
// MAIN COMPONENT
// =====================

const ContactPage: React.FC = () => {
  const [siteSettings, setSiteSettings] = useState<SiteSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formStatus, setFormStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  const colors = getActiveColorTheme();

  useEffect(() => {
    const fetchContactData = async () => {
      try {
        setLoading(true);
        const settings = await api.getSiteSettings();
        setSiteSettings(settings);
      } catch (error) {
        console.error('Failed to fetch site settings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchContactData();
  }, []);

  const handleFormChange = (field: keyof ContactFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleFormSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormSubmitting(true);
    setFormStatus({ type: null, message: '' });

    try {
      // Simulate form submission - replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // For now, just show success message
      setFormStatus({
        type: 'success',
        message: 'Thank you for your message! We will get back to you within 24-48 hours.',
      });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
      });
    } catch (error) {
      setFormStatus({
        type: 'error',
        message: 'Sorry, there was an error sending your message. Please try again or contact us directly.',
      });
    } finally {
      setFormSubmitting(false);
    }
  };

  const getSocialIcon = (platform: string) => {
    switch (platform.toLowerCase()) {
      case 'linkedin': return <LinkedIn fontSize="small" />;
      case 'twitter': return <Twitter fontSize="small" />;
      case 'facebook': return <Facebook fontSize="small" />;
      case 'youtube': return <YouTube fontSize="small" />;
      case 'instagram': return <Instagram fontSize="small" />;
      case 'github': return <GitHub fontSize="small" />;
      default: return <Email fontSize="small" />;
    }
  };

  // Contact info with fallbacks
  const contactInfo = {
    generalEmail: siteSettings?.contactEmail || 'create.program@dal.ca',
    phoneNumber: siteSettings?.contactPhone || '902-494-2451',
    address: 'Faculty of Computer Science\nDalhousie University\n6050 University Avenue\nHalifax, NS B3H 4R2\nCanada',
    officeHours: 'Monday - Friday: 9:00 AM - 5:00 PM',
  };

  // Social media links with fallbacks
  const socialLinks = siteSettings?.socialMediaDefaults?.length > 0 
    ? siteSettings.socialMediaDefaults.filter(link => link.isActive !== false)
    : [
        { platform: 'LinkedIn', url: 'https://www.linkedin.com/school/dalhousie-university/' },
        { platform: 'Twitter', url: 'https://twitter.com/DalCompSci' },
        { platform: 'YouTube', url: 'https://www.youtube.com/user/DalhousieUniv' },
      ];

  return (
    <Box sx={{ minHeight: '100vh', background: colors.background.default }}>
      {/* Hero Section */}
      <HeroSection>
        <Container maxWidth="lg">
          <HeroContent>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 700,
                marginBottom: 2,
                background: colors.gradients.secondary,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Contact Us
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '1.2rem', md: '1.5rem' },
                fontWeight: 400,
                color: colors.text.secondary,
                maxWidth: '600px',
                mx: 'auto',
                lineHeight: 1.5,
              }}
            >
              Get in touch with our team for questions about the program, research opportunities, or partnerships
            </Typography>
          </HeroContent>
        </Container>
      </HeroSection>

      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={4}>
          {/* Contact Form */}
          <Grid size={{ xs: 12, md: 7 }}>
            <ContactFormCard>
              <Typography
                variant="h4"
                sx={{
                  color: colors.brand.accent.main,
                  fontWeight: 700,
                  marginBottom: 3,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Send sx={{ fontSize: '2rem' }} />
                Send us a Message
              </Typography>

              {formStatus.type && (
                <Alert 
                  severity={formStatus.type} 
                  sx={{ mb: 3 }}
                  onClose={() => setFormStatus({ type: null, message: '' })}
                >
                  {formStatus.message}
                </Alert>
              )}

              <Box component="form" onSubmit={handleFormSubmit}>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <StyledTextField
                      fullWidth
                      label="Your Name"
                      variant="outlined"
                      required
                      value={formData.name}
                      onChange={handleFormChange('name')}
                      disabled={formSubmitting}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <StyledTextField
                      fullWidth
                      label="Email Address"
                      type="email"
                      variant="outlined"
                      required
                      value={formData.email}
                      onChange={handleFormChange('email')}
                      disabled={formSubmitting}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <StyledTextField
                      fullWidth
                      label="Subject"
                      variant="outlined"
                      required
                      value={formData.subject}
                      onChange={handleFormChange('subject')}
                      disabled={formSubmitting}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <StyledTextField
                      fullWidth
                      label="Message"
                      multiline
                      rows={6}
                      variant="outlined"
                      required
                      value={formData.message}
                      onChange={handleFormChange('message')}
                      disabled={formSubmitting}
                      helperText="Please provide as much detail as possible about your inquiry."
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <SubmitButton
                      type="submit"
                      variant="contained"
                      disabled={formSubmitting}
                      startIcon={<Send />}
                      sx={{ mt: 2 }}
                    >
                      {formSubmitting ? 'Sending...' : 'Send Message'}
                    </SubmitButton>
                  </Grid>
                </Grid>
              </Box>
            </ContactFormCard>
          </Grid>

          {/* Contact Information */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              
              {/* Direct Contact */}
              <ContactCard>
                <CardContent sx={{ p: 3, flexGrow: 1 }}>
                  <Typography
                    variant="h5"
                    sx={{
                      color: colors.brand.accent.main,
                      fontWeight: 600,
                      marginBottom: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      fontSize: '1.3rem',
                    }}
                  >
                    <Email sx={{ fontSize: '1.3rem' }} />
                    Direct Contact
                  </Typography>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ color: colors.text.secondary, fontWeight: 600, fontSize: '0.95rem', mb:1 }}>
                      General Inquiries: 
                    </Typography>
                    <Typography variant="body2" sx={{color: colors.text.secondary, fontWeight: 500 }}>
                        David Langstroth
                    </Typography>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        color: colors.brand.accent.main, 
                        fontWeight: 500,
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        wordBreak: 'break-word',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                      onClick={() => window.open(`mailto:dll@cs.dal.ca`, '_self')}
                    >
                      {loading ? (
                        <Skeleton variant="text" width={200} sx={{ bgcolor: withOpacity(colors.text.primary, 0.1) }} />
                      ) : (
                        'dll@cs.dal.ca'
                      )}
                    </Typography>
                  </Box>
                </CardContent>
              </ContactCard>

              {/* Location */}
              <ContactCard>
                <CardContent sx={{ p: 3, flexGrow: 1 }}>
                  <Typography
                    variant="h5"
                    sx={{
                      color: colors.brand.accent.main,
                      fontWeight: 600,
                      marginBottom: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      fontSize: '1.3rem',
                    }}
                  >
                    <LocationOn sx={{ fontSize: '1.3rem' }} />
                    Our Location
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      color: colors.text.secondary,
                      lineHeight: 1.5,
                      whiteSpace: 'pre-line',
                      mb: 2,
                      fontSize: '0.85rem',
                    }}
                  >
                    {contactInfo.address}
                  </Typography>

                  <Button
                    variant="outlined"
                    startIcon={<Directions />}
                    onClick={() => window.open('https://maps.google.com/?q=Faculty+of+Computer+Science+Dalhousie+University+Halifax', '_blank')}
                    sx={{
                      color: colors.brand.accent.main,
                      borderColor: colors.brand.accent.main,
                      fontSize: '0.8rem',
                      py: 0.8,
                      px: 2,
                      '&:hover': {
                        borderColor: colors.brand.accent.dark,
                        backgroundColor: withOpacity(colors.brand.accent.main, 0.1),
                      },
                    }}
                  >
                    Get Directions
                  </Button>
                </CardContent>
              </ContactCard>

              {/* Social Media */}
              {socialLinks.length > 0 && (
                <ContactCard>
                  <CardContent sx={{ p: 3, textAlign: 'center' }}>
                    <Typography
                      variant="h5"
                      sx={{
                        color: colors.brand.accent.main,
                        fontWeight: 600,
                        marginBottom: 1.5,
                        fontSize: '1.3rem',
                      }}
                    >
                      Follow Us
                    </Typography>

                    <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                      {socialLinks.map((social, index) => (
                        <SocialIconButton
                          key={index}
                          size="small"
                          onClick={() => window.open(social.url, '_blank', 'noopener,noreferrer')}
                          title={`Follow us on ${social.platform}`}
                          sx={{ p: 1 }}
                        >
                          {getSocialIcon(social.platform)}
                        </SocialIconButton>
                      ))}
                    </Box>
                  </CardContent>
                </ContactCard>
              )}
            </Box>
          </Grid>
        </Grid>

       
        {/* Additional Information */}
        <Box sx={{ 
          mt: 8,
          p: 4,
          background: colors.background.elevated,
          border: `1px solid ${withOpacity(colors.brand.accent.main, 0.2)}`,
          borderRadius: 3,
          textAlign: 'center',
        }}>
          <Typography
            variant="h5"
            sx={{
              color: colors.brand.accent.main,
              fontWeight: 600,
              marginBottom: 2,
            }}
          >
            Visit Our Campus
          </Typography>
          
          <Typography
            variant="body1"
            sx={{
              color: colors.text.secondary,
              lineHeight: 1.7,
              maxWidth: '800px',
              mx: 'auto',
              mb: 3,
            }}
          >
            Located in the heart of Halifax, Nova Scotia, Dalhousie University&apos;s Faculty of Computer Science 
            is home to our NSERC CREATE Cybersecurity Program. We welcome visitors and encourage you to 
            explore our facilities and meet our research teams.
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: colors.text.disabled,
              fontStyle: 'italic',
            }}
          >
            Please contact us in advance to schedule campus visits or meetings with faculty members.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default ContactPage;